import { useCallback } from 'react';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { lookupLocal, fillTemplate } from './brandText';

// ─────────────────────────────────────────────────────────────────────────────
// Strings for the mobile پذیرش redesign (docs/DOCTOR_MOBILE_RECEPTION_REDESIGN.md)
// that the kit's `dashboard` catalogue does not carry yet.
//
// They belong in `packages/clinical/src/i18n/translations/{fa,en,ar}/
// dashboard.json` under the same keys (the three JSON files beside this module
// are shaped as that file's fragment), and will move there with the next kit
// release. Until then they live here, and `useReceptionStrings` asks the kit
// FIRST: the moment a released catalogue has a key, the kit's wording wins and
// this entry is dead — so the move is a deletion, never a behaviour change.
// ─────────────────────────────────────────────────────────────────────────────

// Nested exactly like the kit's `dashboard.json`, so moving them is a copy.
import fa from './receptionStrings/fa.json';
import en from './receptionStrings/en.json';
import ar from './receptionStrings/ar.json';

const flatten = (obj, prefix = '', out = {}) => {
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object') flatten(v, key, out); else out[key] = v;
  }
  return out;
};

const STRINGS = { fa: flatten(fa), en: flatten(en), ar: flatten(ar) };

/**
 * Look a key up in the local table — exported for tests. The brand's overlay
 * and tokens apply as in the kit's catalogues (./brandText.js).
 */
export function localString(locale, key, vars) {
  const { value, from } = lookupLocal(STRINGS, locale, key);
  return value == null ? key : fillTemplate(value, vars, from);
}

/**
 * `t` for this screen: the kit catalogue when it has the key, the local table
 * otherwise. Numbers are formatted by the caller (`formatNumber`/`formatDigits`)
 * before they are passed in, so both paths render the same digits.
 */
export function useReceptionStrings() {
  const { t, has, locale } = useTranslation();
  return useCallback(
    (key, vars) => (has(key) ? t(key, vars) : localString(locale, key, vars)),
    [t, has, locale],
  );
}

export const RECEPTION_STRING_LOCALES = Object.keys(STRINGS);
export const RECEPTION_STRING_KEYS = Object.keys(STRINGS.fa);
